import { useEffect, useRef, useState } from 'react';
import { probeServerAlive } from '../utils/errorTaxonomy';
import { computeCenteredBackoff } from '../utils/resilientFetch';

/**
 * Keeps trying, on an exponentially widening interval, while a failure is on
 * screen.
 *
 * `resilientFetch` already backs off — but only within one request, for four
 * attempts over roughly fifteen seconds. That is sized for the case it was built
 * for: a deploy, which takes 30-120 seconds. Once those attempts are spent the
 * request gives up and the fallback settles into a terminal state, waiting for a
 * click. Someone who stepped away during a longer outage came back to a page
 * that had stopped trying, even though the server had been up for ten minutes.
 *
 * So this is the same algorithm one layer up — `delay = base * 2^n`, capped —
 * applied to the screen rather than the request.
 *
 * The jitter is centred rather than sampled from zero. Full jitter halves the
 * mean wait, which is right when attempts race inside one request and finishing
 * early is the point; it is wrong for polling something that is down. If the
 * server did not recover in five seconds it is unlikely to recover in seven, so
 * probes drawn from [0, 5s] are spent where they pay off least. Centring keeps
 * the intended pace and still decorrelates clients: ±2s scatters them over four
 * seconds, ample against every tab firing at the same millisecond.
 *
 * With a 5s base and a 15-minute ceiling the probes land near 5s, 10s, 20s, 40s,
 * 80s, 2.7m, 5.3m, 10.7m, then every 15 minutes — dense through the window where
 * most outages end, sparse once it is clear this one has not.
 */

const DEFAULT_BASE_DELAY_MS = 5000;
const DEFAULT_MAX_DELAY_MS = 15 * 60 * 1000;

/**
 * How far into the current outage we are, shared by every component.
 *
 * The attempt counter used to live inside the effect, which meant it reset every
 * time the hook remounted. Switching tab unmounts one fallback and mounts
 * another, so a vendor clicking between Orders tabs during an outage held the
 * whole app at the first interval — five seconds, indefinitely, from every open
 * page. That is the opposite of what backoff is for, and it arrives precisely
 * when the server can least afford it.
 *
 * An outage is a property of the server, not of whichever component happens to
 * be mounted, so the elapsed time lives here and a newly mounted fallback
 * inherits it. It resets only when a probe actually succeeds.
 */
let outageStartedAt = null;
let outageAttempts = 0;
/**
 * When the pending probe fires, shared for the same reason.
 *
 * The countdown is what the user reads, so it has to survive navigation too:
 * moving from Orders to Growth Suite or SSN mounts a different fallback, and a
 * timer that restarts there tells them the wait got longer when it did not.
 * There is one probe in flight for the whole app, so there is one countdown.
 */
let outageNextProbeAt = null;
/** Mounted fallbacks, so a new probe time reaches all of them at once. */
const countdownListeners = new Set();
/**
 * The one pending probe, and the timer holding it.
 *
 * Both live here rather than in a component. A dashboard can show several
 * fallbacks at once and during an outage every open page has one, so they must
 * share a single train: otherwise the app's request rate scales with how many
 * panels happen to be mounted, nearly all of it redundant since one answer
 * serves everyone.
 *
 * Keeping the *timer* at module scope matters just as much. When it belonged to
 * the component, unmounting cancelled it and the replacement scheduled a new
 * one — which advanced the attempt counter without any probe having fired. A
 * user switching Orders tabs therefore watched the countdown jump from 19s to a
 * minute to five, each switch skipping a rung of the backoff. The wait is a
 * property of the outage, so navigation must neither reset it nor advance it.
 */
let probeTrainRunning = false;
let probeTimer = null;
/** Who to tell when the probe succeeds; replaced as components come and go. */
let onRecoverAll = () => {};

function publishNextProbeAt(at) {
  outageNextProbeAt = at;
  countdownListeners.forEach((fn) => fn(at));
}

/**
 * Schedules the next probe and keeps going until one succeeds.
 *
 * Deliberately not tied to a component: mounting and unmounting fallbacks may
 * come and go while this runs, and the schedule is unaffected by either.
 */
function runProbeTrain(opts) {
  if (probeTrainRunning) return;
  probeTrainRunning = true;

  const step = () => {
    const delay = computeCenteredBackoff(outageAttempts, opts);
    outageAttempts += 1;
    publishNextProbeAt(Date.now() + delay);

    probeTimer = setTimeout(async () => {
      let alive = false;
      try {
        alive = await probeServerAlive();
      } catch {
        alive = false;
      }
      if (alive) {
        probeTrainRunning = false;
        probeTimer = null;
        clearOutage();
        // Reload rather than declaring victory: /health/ does not check the
        // database, so the request itself is the real test.
        onRecoverAll();
      } else {
        step();
      }
    }, delay);
  };

  step();
}

/** One immediate check, without disturbing the pacing. */
function probeNow() {
  if (!probeTrainRunning) return;
  probeServerAlive()
    .then((alive) => {
      if (!alive) return;
      clearTimeout(probeTimer);
      probeTimer = null;
      probeTrainRunning = false;
      clearOutage();
      onRecoverAll();
    })
    .catch(() => {});
}

/** Exported for tests, which must not inherit an outage from a previous case. */
export function resetOutageClock() {
  outageStartedAt = null;
  outageAttempts = 0;
  outageNextProbeAt = null;
}

/** Called when a probe succeeds: the next outage starts its own clock. */
function clearOutage() {
  outageStartedAt = null;
  outageAttempts = 0;
  publishNextProbeAt(null);
}

/** Exported for tests. */
export function _resetProbeTrain() {
  clearTimeout(probeTimer);
  probeTimer = null;
  probeTrainRunning = false;
}

/**
 * @param {boolean} active        Whether a failure is currently on screen.
 * @param {() => void} onRecover  Called when the server answers again.
 * @param {object} [options]
 * @param {number} [options.baseDelayMs=5000]
 * @param {number} [options.maxDelayMs=900000]  15 minutes.
 */
export function useAutoRecover(active, onRecover, options = {}) {
  const {
    baseDelayMs = DEFAULT_BASE_DELAY_MS,
    maxDelayMs = DEFAULT_MAX_DELAY_MS,
    jitterMs = 2000,
    random,
  } = options;

  // Seeded from the shared value, so a fallback mounted mid-outage shows the
  // countdown already running rather than starting a new one. Only when this
  // one is actually participating: a fallback that opted out, or whose failure
  // no retry could fix, must not display somebody else's timer.
  const [nextProbeAt, setNextProbeAt] = useState(active ? outageNextProbeAt : null);
  const onRecoverRef = useRef(onRecover);
  onRecoverRef.current = onRecover;

  // Every mounted fallback follows the same probe, so they all count down
  // together and navigating between them is seamless.
  useEffect(() => {
    if (!active) {
      setNextProbeAt(null);
      return undefined;
    }
    countdownListeners.add(setNextProbeAt);
    setNextProbeAt(outageNextProbeAt);
    return () => countdownListeners.delete(setNextProbeAt);
  }, [active]);

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    // First failure of this outage starts the clock; later mounts join it.
    if (outageStartedAt === null) {
      outageStartedAt = Date.now();
      outageAttempts = 0;
    }

    // The train itself lives at module scope, so it survives this component
    // unmounting and is not restarted by the next one mounting. Navigation
    // neither resets the wait nor skips it forward.
    onRecoverAll = () => onRecoverRef.current();
    runProbeTrain({ baseDelayMs, maxDelayMs, jitterMs, random });

    // Returning to the page, or the browser reporting the network is back, are
    // both worth one immediate check — but not a change of pace. Resetting the
    // interval here would hand back the same loophole through another door.
    const checkNow = () => {
      if (document.visibilityState !== 'visible') return;
      probeNow();
    };

    document.addEventListener('visibilitychange', checkNow);
    window.addEventListener('online', checkNow);

    return () => {
      // Deliberately leaves the train running: another fallback may still be
      // mounted, and even if none is, the outage has not ended just because
      // this component went away.
      document.removeEventListener('visibilitychange', checkNow);
      window.removeEventListener('online', checkNow);
    };
  }, [active, baseDelayMs, maxDelayMs, jitterMs, random]);

  return { nextProbeAt, outageStartedAt };
}

export default useAutoRecover;
